// The panel's line list: a row per line (or per folded group of minor
// lines), each with its count and status, the group headings that show or
// hide their lines, the operator's words under a row, and the summary over
// it all. A city's own rows (London's National Rail) and their counts come
// in as the list's extension, counted with the rest.

import type { Box, TrainCounts } from '@tol/core/onscreen';
import type { LineId, PageLine } from '@tol/core/types';
import { el, esc, lineVars } from './dom.ts';
import type { ShellState } from './state.ts';

export interface RosterLine extends PageLine {
  readonly rosterId?: string;
  readonly vehicleMode?: 'coach';
}

// A line's row in the list: its status text, red when it's bad, and the
// detail shown on hover.
export interface RosterStatus {
  readonly text: string;
  readonly bad: boolean;
  readonly title: string;
  // Which of the statuses it is, for its color; default none (the text's
  // own, red when bad).
  readonly kind?: StatusKind;
  // What the operator says, shown under the row when its status is tapped.
  readonly detail?: readonly StatusDetail[];
}

// Good service, delays, part of the line closed, all of it closed, planned
// works, or something to know.
export type StatusKind = 'good' | 'delays' | 'part' | 'closed' | 'planned' | 'info';

// One of the operator's messages about a line.
export interface StatusDetail {
  // Its type: "Part closure".
  readonly title: string;
  // Its headline.
  readonly text: string;
  // The rest of it.
  readonly more?: string;
}

// The detail under a line's row: each message, its type in bold.
export function statusDetailHtml(detail: readonly StatusDetail[]): string {
  return detail
    .map(
      (d) =>
        `<p><strong>${esc(d.title)}</strong> ${esc(d.text)}</p>` +
        (d.more === undefined || d.more === '' ? '' : `<p class="more">${esc(d.more)}</p>`),
    )
    .join('');
}

// A group of lines in the list under a heading that shows or hides them all.
export interface LineGroup {
  readonly id: string;
  readonly name: string;
  readonly lines: readonly LineId[];
  readonly modeName?: string;
}

// Which of the lines the list has rows for, and a key that changes when
// that does (the list is made again only then).
export interface RosterScope {
  readonly key: string;
  readonly keep: RosterKeep;
  readonly first?: RosterKeep;
}

export interface Noun {
  readonly one: string;
  readonly many: string;
}
const TRAINS: Noun = { one: 'train', many: 'trains' };

// One formatter: toLocaleString makes one per call, and the list counts
// hundreds of rows each time the camera stops.
const NUMBER = new Intl.NumberFormat('en-US');
const num = (n: number): string => (n < 1000 ? String(n) : NUMBER.format(n));

// The panel's summary: "<strong>12</strong> trains running on 3 lines", and
// how many of them are scheduled (no live data), so the count doesn't
// claim more live coverage than there is. With some of the `total` out of
// view, it counts the n in view, on the lines they're on, and says so:
// "<strong>412</strong> of 1,208 trains in view, on 12 lines".
export function summaryHtml(n: number, lines: number, noun: Noun = TRAINS, scheduled = 0, total = n): string {
  const sched = scheduled > 0 ? ` (${num(scheduled)} scheduled)` : '';
  const onLines = `${num(lines)} available ${lines === 1 ? 'line' : 'lines'}`;
  if (total === n)
    return `<strong>${num(n)}</strong> ${n === 1 ? noun.one : noun.many} tracked across ${onLines}${sched}`;
  return `<strong>${num(n)}</strong> of ${num(total)} ${noun.one} positions in this area${n > 0 ? `, across ${onLines}` : ''}${sched}`;
}

// How the vehicles in view are placed, one line per way, each naming the
// systems most of whose vehicles in view it places: "GPS: exo, Buses",
// "Estimated: U-Bahn", "Timetable: Métro, REM". A system's hidden lines don't
// count; a system with nothing in view isn't named. A page of one unnamed
// group says only how. Empty with nothing in view.
export type Placement = 'gps' | 'estimated' | 'timetable';
const PLACEMENTS: readonly { readonly id: Placement; readonly label: string; readonly title: string }[] = [
  { id: 'gps', label: 'GPS', title: 'Placed by its own GPS' },
  {
    id: 'estimated',
    label: 'Estimated',
    title: 'Placed between stops from its predicted times, not its GPS',
  },
  { id: 'timetable', label: 'Timetable', title: 'Placed by the timetable: no live data' },
];
export function accuracyHtml(
  groups: readonly LineGroup[],
  counts: { readonly [K in keyof TrainCounts]: Readonly<TrainCounts[K]> },
  hidden: ReadonlySet<LineId> = new Set(),
  label?: (name: string) => string,
): string {
  const named = new Map<Placement, string[]>(PLACEMENTS.map((p) => [p.id, []]));
  const any = new Set<Placement>();
  for (const g of groups) {
    let n = 0;
    let gps = 0;
    let timetable = 0;
    for (const id of g.lines) {
      if (hidden.has(id)) continue;
      n += counts.counts[id] ?? 0;
      gps += counts.gps[id] ?? 0;
      timetable += counts.scheduled[id] ?? 0;
    }
    if (n === 0) continue;
    const estimated = n - gps - timetable;
    // The way most of them are placed; a tie goes to the better one.
    const way: Placement =
      gps >= estimated && gps >= timetable ? 'gps' : estimated >= timetable ? 'estimated' : 'timetable';
    any.add(way);
    // As the system calls itself, without the world map's city prefix or a
    // "(scheduled)" the way it's placed already says ("Métro (scheduled)").
    const name = g.modeName ?? g.name;
    const system = name
      .slice(name.lastIndexOf(': ') + 1)
      .replace(/\s*\(scheduled\)$/iu, '')
      .trim();
    const noun = label?.(system) ?? system;
    const list = named.get(way);
    if (list && noun !== '' && !list.includes(noun)) list.push(noun);
  }
  return PLACEMENTS.filter((p) => any.has(p.id))
    .map((p) => {
      const names = named.get(p.id) ?? [];
      const text = names.length > 0 ? `${p.label}: ${names.map((x) => esc(x)).join(', ')}` : p.label;
      return `<span class="placed" title="${p.title}"><i class="placed-${p.id}" aria-hidden="true"></i>${text}</span>`;
    })
    .join('');
}

const STATUS_KINDS: readonly StatusKind[] = ['good', 'delays', 'part', 'closed', 'planned', 'info'];

// A line's row in the list; `folded`: the group whose fold it's behind
// (the row exists only while that fold is open).
const row = (
  l: {
    readonly id: string;
    readonly name: string;
    readonly show: string;
    readonly dual?: boolean;
  },
  folded = '',
): string =>
  `<li${folded === '' ? '' : ` data-in="${esc(folded)}"`}><button class="row${l.dual === true ? ' dual' : ''}" data-line="${l.id}" style="${lineVars(l.show)}" aria-pressed="true">
        <span class="bar"></span><span class="name">${esc(l.name)}</span><span class="count"></span><span class="status"></span>
      </button></li>`;

// A named group of minor lines (buses, minor.ts) is folded: one "All
// buses" row for the whole group, which shows or hides all of it, and its
// lines behind "Each line" on its heading. An unnamed group has no heading
// to hold "Each line" and no name for the row, so it lists each line.
export interface RosterGroup extends LineGroup {
  readonly folded: boolean;
}

// The line list's groups: the city's, or all its lines under no heading.
// The list, its counts and its clicks all use this one list.
export function rosterGroups(lines: readonly PageLine[], groups?: readonly LineGroup[]): readonly RosterGroup[] {
  const minor = new Set(lines.filter((l) => l.minor === true).map((l) => l.id));
  return (groups ?? [{ id: '', name: '', lines: lines.map((l) => l.id) }]).map((g) =>
    Object.assign({}, g, {
      folded: g.name !== '' && g.lines.length > 0 && g.lines.every((id) => minor.has(id)),
    }),
  );
}

// A folded group's row names what the group is, without the world map's
// city prefix: "Portland: Buses" is "All buses".
export function groupNoun(name: string): string {
  // Acronyms (Calgary's MAX) keep their capitals: "All MAX", "All buses".
  return name
    .slice(name.lastIndexOf(': ') + 1)
    .trim()
    .split(' ')
    .map((w) => (w.length > 1 && w === w.toUpperCase() ? w : w.toLowerCase()))
    .join(' ');
}

// What a count counts: minor lines are buses (minor.ts), the rest trains.
// `scheduled` of them run by the timetable with no live data: a line whose
// every vehicle does says "3 scheduled", a line with some "12 trains, 3 sched.".
// With some of the line's `total` out of view, n are the ones in view (and
// `scheduled` of those): "3 of 34 trains", "0 of 12 buses".
export function countText(n: number, minor: boolean, scheduled = 0, total = n, vehicleMode?: 'coach'): string {
  const noun = (k: number): string =>
    vehicleMode === 'coach'
      ? k === 1
        ? 'coach'
        : 'coaches'
      : minor
        ? k === 1
          ? 'bus'
          : 'buses'
        : k === 1
          ? 'train'
          : 'trains';
  const sched = scheduled > 0 ? `, ${num(scheduled)} sched.` : '';
  if (total !== n) return `${num(n)} of ${num(total)} ${noun(total)}${sched}`;
  if (scheduled > 0 && scheduled >= n) return `${num(n)} scheduled`;
  return `${num(n)} ${noun(n)}${sched}`;
}

// Which rows the list has: a line's or a group's id kept or not.
export type RosterKeep = (id: string) => boolean;
const keepAll: RosterKeep = () => true;

export function rosterRows(
  g: RosterGroup,
  lineStyle: (id: LineId) => RosterLine,
  keep: RosterKeep = keepAll,
): readonly { readonly id: LineId; readonly ids: readonly LineId[] }[] {
  const rows = new Map<string, { id: LineId; ids: LineId[] }>();
  for (const id of g.lines) {
    if (!keep(id)) continue;
    const key = lineStyle(id).rosterId ?? id;
    const existing = rows.get(key);
    if (existing) existing.ids.push(id);
    else rows.set(key, { id, ids: [id] });
  }
  return [...rows.values()];
}

export function combinedStatus(
  ids: readonly LineId[],
  status: ((id: LineId) => RosterStatus | undefined) | undefined,
): RosterStatus | undefined {
  const all = ids.flatMap((id) => status?.(id) ?? []);
  if (all.length < 2) return all[0];
  const notices = all.filter((s) => s.kind !== 'good');
  const shown = notices.length > 0 ? notices : all;
  const order: readonly StatusKind[] = ['closed', 'part', 'delays', 'planned', 'info', 'good'];
  const kind = order.find((k) => shown.some((s) => s.kind === k));
  return {
    text: [...new Set(shown.map((s) => s.text).filter(Boolean))].join(' · '),
    bad: shown.some((s) => s.bad),
    title: [...new Set(shown.map((s) => s.title).filter(Boolean))].join('\n'),
    ...(kind === undefined ? {} : { kind }),
    detail: [...new Map(all.flatMap((s) => s.detail ?? []).map((d) => [JSON.stringify(d), d])).values()],
  };
}

// A folded group's lines' rows, made when its fold opens.
export function foldedRows(g: RosterGroup, lineStyle: (id: LineId) => PageLine, keep: RosterKeep = keepAll): string {
  return rosterRows(g, lineStyle, keep)
    .map(({ id }) => row(lineStyle(id), g.id))
    .join('');
}

// A group's rows in the line list: those `keep` keeps, and a folded group's
// lines only when `open` (a city's hundreds of buses are rows only once
// asked for).
export function groupRows(
  g: RosterGroup,
  lineStyle: (id: LineId) => PageLine,
  keep: RosterKeep = keepAll,
  open = false,
): string {
  const ids = g.lines.filter((id) => keep(id));
  const heading =
    g.name === '' || !keep(g.id)
      ? ''
      : `<li class="group"><button class="linkish group-toggle" data-group="${esc(g.id)}">${esc(g.name)}</button>` +
        (g.folded
          ? `<button class="linkish group-expand" data-expand="${esc(g.id)}" aria-expanded="${String(open)}">Each line</button>`
          : '') +
        '</li>';
  if (!g.folded)
    return (
      heading +
      rosterRows(g, lineStyle, keep)
        .map(({ id }) => row(lineStyle(id)))
        .join('')
    );
  const first = lineStyle(ids[0] ?? g.lines[0] ?? '');
  return (
    heading +
    (keep(g.id)
      ? `<li><button class="row group-row" data-group="${esc(g.id)}" style="${lineVars(first.show)}" aria-pressed="true">
        <span class="bar"></span><span class="name">All ${esc(groupNoun(g.modeName ?? g.name))}</span><span class="count"></span><span class="status">${num(ids.length)} ${ids.length === 1 ? 'line' : 'lines'}, shown zoomed in</span>
      </button></li>`
      : '') +
    (open ? foldedRows(g, lineStyle, keep) : '')
  );
}

// The focused control in the list, found again in a new list by what it's
// for (a keyboard's place survives the list being made again).
function focusedInRoster(ul: HTMLElement): string | null {
  const a = document.activeElement;
  if (!(a instanceof HTMLElement) || !ul.contains(a)) return null;
  for (const attr of ['data-line', 'data-group', 'data-expand', 'data-detail']) {
    const v = a.getAttribute(attr);
    if (v !== null) return `${a.tagName.toLowerCase()}.${a.classList[0] ?? ''}[${attr}="${CSS.escape(v)}"]`;
  }
  return null;
}

// The roster row an event happened in, and its line id.
function rosterRow(e: Event): { readonly btn: HTMLElement; readonly id: LineId } | null {
  const btn = e.target instanceof Element ? e.target.closest('.row') : null;
  if (!(btn instanceof HTMLElement)) return null;
  const id = btn.dataset['line'];
  return id === undefined ? null : { btn, id };
}

const setText = (node: HTMLElement, text: string): void => {
  if (node.textContent !== text) node.textContent = text;
};
const setAttribute = (node: HTMLElement, name: string, value: string): void => {
  if (node.getAttribute(name) !== value) node.setAttribute(name, value);
};
const setClass = (node: HTMLElement, name: string, on: boolean): void => {
  if (node.classList.contains(name) !== on) node.classList.toggle(name, on);
};

// A row of a city's own after its lines, with a fixed status text.
export interface ExtraRow {
  readonly id: string;
  readonly name: string;
  readonly show: string;
  readonly status?: string;
}

// The list's extension: a city's own rows and their train counts (null: no
// data yet), also counted in the summary; only those in the box when given
// one (the map's view).
export interface RosterExtension {
  readonly rows: (() => readonly ExtraRow[]) | undefined;
  readonly counts: ((within?: Box) => ReadonlyMap<string, number> | null) | undefined;
}

export interface RosterOptions {
  // The shell's, which it grows (addLines).
  readonly lines: readonly RosterLine[];
  readonly line: Readonly<Partial<Record<LineId, RosterLine>>>;
  readonly groups: readonly RosterGroup[];
  readonly accuracyGroups?: readonly LineGroup[] | undefined;
  readonly accuracyLabel?: ((name: string) => string) | undefined;
  readonly lineStyle: (id: LineId) => RosterLine;
  // The shown lines, the focused one and the counts, all and in view.
  readonly state: ShellState;
  // Lines the network lacks, or has no track for: "no data".
  readonly missing: ReadonlySet<LineId>;
  // The map's view as last counted (null: not yet).
  readonly inViewBox: () => Box | null;
  readonly extension: RosterExtension;
  readonly noun: Noun | undefined;
  readonly loading: (() => boolean) | undefined;
  readonly status: ((id: LineId) => RosterStatus | undefined) | undefined;
  readonly idle: ((id: LineId) => string | undefined) | undefined;
  readonly scope: RosterScope | undefined;
  // Show or hide lines on the map, and highlight one (null: none).
  readonly setLinesVisible: (ids: readonly LineId[], visible: boolean) => void;
  readonly setFocus: (id: LineId | null) => void;
}

export interface Roster {
  // camera: the camera moved; the counts changed, the statuses didn't.
  readonly render: (camera?: boolean) => void;
  // The list has rows for these lines only, from now on.
  readonly setScope: (scope: RosterScope) => void;
  // Groups of lines the shell just added: the list is made again.
  readonly addGroups: (groups: readonly RosterGroup[], accuracyGroups?: readonly LineGroup[]) => void;
}

export function createRoster(options: RosterOptions): Roster {
  const { state, extension, line: LINE, lineStyle } = options;
  const GROUPS = [...options.groups];
  const ACCURACY_GROUPS = [...(options.accuracyGroups ?? options.groups)];

  // The shown lines' trains: all of them, and those in view, and the lines
  // those are on. Once some are out of view, the summary counts those in it.
  function renderSummary(
    extraAll: ReadonlyMap<string, number> | null,
    extraIn: ReadonlyMap<string, number> | null,
  ): void {
    const inView = state.inView ?? state;
    let total = 0;
    let n = 0;
    let scheduled = 0;
    const linesInView = new Set<string>();
    for (const [id, count = 0] of Object.entries(state.counts)) {
      if (state.hidden.has(id)) continue;
      total += count;
      const k = inView.counts[id] ?? 0;
      n += k;
      if (k > 0) linesInView.add(LINE[id]?.rosterId ?? id);
      scheduled += inView.scheduled[id] ?? 0;
    }
    const lines = new Set(
      options.lines.filter((l) => !state.hidden.has(l.id) && !options.missing.has(l.id)).map((l) => l.rosterId ?? l.id),
    );
    for (const [id, count] of extraAll ?? []) {
      if (state.hidden.has(id)) continue;
      total += count;
      lines.add(LINE[id]?.rosterId ?? id);
      const k = extraIn?.get(id) ?? 0;
      n += k;
      if (k > 0) linesInView.add(LINE[id]?.rosterId ?? id);
    }
    const summary = el('#summary');
    const html =
      total === 0 && options.loading?.() === true
        ? 'Loading live trains…'
        : summaryHtml(n, n === total ? lines.size : linesInView.size, options.noun, scheduled, total);
    if (summary.innerHTML !== html) summary.innerHTML = html;
    const accuracy = el('#accuracy');
    const how = accuracyHtml(ACCURACY_GROUPS, inView, state.hidden, options.accuracyLabel);
    if (accuracy.innerHTML !== how) accuracy.innerHTML = how;
  }

  // The list has rows only for the lines in scope (the world map's city in
  // view) and a folded group's lines only while it's open, so a page with
  // thousands of lines has tens of rows. It's made again when the scope
  // changes; its row nodes are kept instead of searched for on every update.
  const rosterNodes = new Map<string, { btn: HTMLElement; count: HTMLElement; status: HTMLElement }>();
  const rowLines = new Map<LineId, readonly LineId[]>();
  const groupNodes = new Map<string, { btn: HTMLElement; count: HTMLElement }>();
  let rosterScope: RosterScope = options.scope ?? { key: '', keep: keepAll };
  let rosterBuilt = false;
  // Folded groups opened ("Each line") and lines whose operator's words are
  // open: kept here, not in rows, so they outlive the rows.
  const openGroups = new Set<string>();
  const openDetails = new Set<LineId>();
  function indexRows(ul: HTMLElement): void {
    rosterNodes.clear();
    groupNodes.clear();
    rowLines.clear();
    for (const g of GROUPS) for (const r of rosterRows(g, lineStyle, rosterScope.keep)) rowLines.set(r.id, r.ids);
    for (const btn of ul.querySelectorAll<HTMLElement>('.row')) {
      const id = btn.dataset['line'];
      const group = btn.dataset['group'];
      if (id !== undefined) rosterNodes.set(id, { btn, count: el('.count', btn), status: el('.status', btn) });
      else if (group !== undefined) groupNodes.set(group, { btn, count: el('.count', btn) });
    }
  }
  function buildRoster(): string | null {
    const ul = el('#roster');
    const focused = focusedInRoster(ul);
    const { keep, first } = rosterScope;
    const groups = first ? GROUPS.toSorted((a, b) => Number(first(b.id)) - Number(first(a.id))) : GROUPS;
    ul.innerHTML =
      groups.map((g) => groupRows(g, lineStyle, keep, openGroups.has(g.id))).join('') +
      (extension.rows?.() ?? [])
        .filter((r) => keep(r.id) && !GROUPS.some((g) => g.lines.includes(r.id)))
        .map((r) => row(r))
        .join('');
    indexRows(ul);
    rosterBuilt = true;
    return focused;
  }
  function setScope(scope: RosterScope): void {
    if (rosterBuilt && scope.key === rosterScope.key) return;
    rosterScope = scope;
    rosterBuilt = false;
    render();
  }
  // A folded group's lines' rows, made or dropped in place (the "Each line"
  // button keeps its focus).
  function setFoldOpen(expand: HTMLElement, groupId: string, open: boolean): void {
    const g = GROUPS.find((x) => x.id === groupId);
    if (!g) return;
    expand.setAttribute('aria-expanded', String(open));
    const ul = el('#roster');
    for (const li of ul.querySelectorAll<HTMLElement>(`li[data-in="${CSS.escape(groupId)}"]`)) {
      const id = li.querySelector<HTMLElement>('.row')?.dataset['line'];
      if (id !== undefined) rosterNodes.delete(id);
      li.remove();
    }
    if (open) openGroups.add(groupId);
    else openGroups.delete(groupId);
    if (!open) return;
    const after =
      ul.querySelector(`.group-row[data-group="${CSS.escape(groupId)}"]`)?.closest('li') ?? expand.closest('li');
    if (!after) return;
    after.insertAdjacentHTML('afterend', foldedRows(g, lineStyle, rosterScope.keep));
    indexRows(ul);
    render();
  }
  // A line's operator's words under its row, and the button that opens them
  // (a button of its own: the row's button shows or hides the line).
  function syncDetail(id: LineId, btn: HTMLElement, name: string, detail: readonly StatusDetail[]): void {
    const li = btn.parentElement;
    if (!li) return;
    let toggle = li.querySelector<HTMLElement>(':scope > .detail-toggle');
    let open = li.querySelector<HTMLElement>(':scope > .alert-detail');
    if (detail.length === 0) {
      toggle?.remove();
      open?.remove();
      openDetails.delete(id);
      return;
    }
    if (!toggle) {
      toggle = document.createElement('button');
      toggle.className = 'detail-toggle';
      toggle.dataset['detail'] = id;
      toggle.setAttribute('aria-label', `What the operator says about ${name}`);
      btn.after(toggle);
    }
    const isOpen = openDetails.has(id);
    setAttribute(toggle, 'aria-expanded', String(isOpen));
    if (!isOpen) {
      open?.remove();
      return;
    }
    if (!open) {
      open = document.createElement('div');
      open.className = 'alert-detail';
      li.append(open);
    }
    const html = statusDetailHtml(detail);
    if (detailHtml.get(open) !== html) {
      open.innerHTML = html;
      detailHtml.set(open, html);
    }
  }
  // Compare authored HTML, not the browser's normalized serialization (quotes
  // and entities differ). Weak keys do not retain closed detail nodes.
  const detailHtml = new WeakMap<HTMLElement, string>();
  // Why each line has no trains (options.idle): a timetable lookup per line,
  // so asked again when trains arrive, not each time the camera stops.
  const idleText = new Map<string, string | undefined>();
  function render(camera = false): void {
    const keep = rosterScope.keep;
    const inView = state.inView ?? state;
    // A new list has no statuses yet.
    const countsOnly = camera && rosterBuilt;
    const focused = rosterBuilt ? null : buildRoster();
    const box = options.inViewBox();
    const extraCounts = extension.counts ? extension.counts() : undefined;
    const extraIn = extension.counts && box ? extension.counts(box) : extraCounts;
    for (const g of GROUPS) {
      const nodes = groupNodes.get(g.id);
      if (!nodes) continue;
      const { btn, count } = nodes;
      let total = 0;
      let n = 0;
      let scheduled = 0;
      const ids = g.lines.filter((id) => keep(id));
      for (const id of ids) {
        if (state.hidden.has(id)) continue;
        total += (state.counts[id] ?? 0) + (extraCounts?.get(id) ?? 0);
        n += (inView.counts[id] ?? 0) + (extraIn?.get(id) ?? 0);
        scheduled += inView.scheduled[id] ?? 0;
      }
      setAttribute(btn, 'aria-pressed', String(ids.some((id) => !state.hidden.has(id))));
      setClass(btn, 'away', n === 0 && total > 0);
      setText(count, countText(n, g.folded, scheduled, total, LINE[g.lines[0] ?? '']?.vehicleMode));
    }
    // A row counts its trains in view, "3 of 34 trains", once some are out
    // of it, and dims with none in it; it stays, for its status and toggle.
    const extraRows = new Map((extension.rows?.() ?? []).map((r) => [r.id, r]));
    const extraIds = new Set(extraRows.keys());
    for (const [id, { btn, count, status: statusEl }] of rosterNodes) {
      const ids = rowLines.get(id) ?? [id];
      const isExtra = extraIds.has(id);
      const total = ids.reduce((sum, line) => sum + (extraCounts?.get(line) ?? 0) + (state.counts[line] ?? 0), 0);
      const n = ids.reduce((sum, line) => sum + (extraIn?.get(line) ?? 0) + (inView.counts[line] ?? 0), 0);
      const scheduled = ids.reduce((sum, line) => sum + (inView.scheduled[line] ?? 0), 0);
      setAttribute(btn, 'aria-pressed', String(ids.some((line) => !state.hidden.has(line))));
      setClass(btn, 'away', n === 0 && total > 0);
      let idle: string | undefined;
      if (total === 0 && !isExtra) {
        if (!countsOnly || !idleText.has(id))
          idleText.set(
            id,
            ids.map((line) => options.idle?.(line)).find((text) => text !== undefined),
          );
        idle = idleText.get(id);
      }
      setText(
        count,
        (ids.every((line) => options.missing.has(line)) && !isExtra) ||
          (isExtra && extraCounts === null && LINE[id] === undefined)
          ? 'no data'
          : (idle ?? countText(n, LINE[id]?.minor === true, scheduled, total, LINE[id]?.vehicleMode)),
      );
      if (countsOnly) continue;
      // Reconcile every field, including cleared alerts, but leave unchanged
      // DOM alone so polling does not trigger needless style/layout work.
      const st = combinedStatus(ids, options.status);
      setText(statusEl, st?.text ?? extraRows.get(id)?.status ?? '');
      setClass(statusEl, 'bad', st?.bad === true);
      for (const k of STATUS_KINDS) setClass(statusEl, `st-${k}`, st?.kind === k);
      const detail = st?.detail ?? [];
      setClass(statusEl, 'has-detail', detail.length > 0);
      if (btn.title !== (st?.title ?? '')) btn.title = st?.title ?? '';
      syncDetail(id, btn, LINE[id]?.name ?? extraRows.get(id)?.name ?? id, detail);
    }
    renderSummary(extraCounts ?? null, extraIn ?? null);
    if (focused !== null) el('#roster').querySelector<HTMLElement>(focused)?.focus();
  }

  const allIds = (): LineId[] =>
    [...new Set([...options.lines.map((l) => l.id), ...(extension.rows?.() ?? []).map((r) => r.id)])].filter((id) =>
      rosterScope.keep(id),
    );

  el('#roster').addEventListener('click', (e) => {
    // A tap on a line's status opens (or closes) what the operator says,
    // rather than hiding the line: a phone has no hover for the title.
    // Its own button for a keyboard.
    const target = e.target instanceof Element ? e.target : null;
    const status = target?.closest('.status.has-detail, .detail-toggle');
    const detailId = status?.closest('li')?.querySelector<HTMLElement>(':scope > .row')?.dataset['line'];
    if (detailId !== undefined) {
      if (openDetails.has(detailId)) openDetails.delete(detailId);
      else openDetails.add(detailId);
      render();
      return;
    }
    const expand = target?.closest<HTMLElement>('[data-expand]');
    const folded = expand?.dataset['expand'];
    if (expand && folded !== undefined) {
      setFoldOpen(expand, folded, expand.getAttribute('aria-expanded') !== 'true');
      return;
    }
    const heading = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-group]') : null;
    const groupId = heading?.dataset['group'];
    if (groupId !== undefined) {
      // A group's heading toggles only the lines retained for this view.
      const ids = (GROUPS.find((g) => g.id === groupId)?.lines ?? []).filter((id) => rosterScope.keep(id));
      options.setLinesVisible(
        ids,
        ids.some((id) => state.hidden.has(id)),
      );
      return;
    }
    const r = rosterRow(e);
    if (!r) return;
    const ids = rowLines.get(r.id) ?? [r.id];
    options.setLinesVisible(
      ids,
      ids.every((id) => state.hidden.has(id)),
    );
  });
  el('#roster').addEventListener('mouseover', (e) => {
    const hovered = rosterRow(e)?.id ?? null;
    const id = hovered !== null && state.hidden.has(hovered) ? null : hovered;
    if (id !== state.focus) options.setFocus(id);
  });
  el('#roster').addEventListener('mouseleave', () => {
    if (state.focus !== null) options.setFocus(null);
  });
  for (const b of document.querySelectorAll('[data-all]')) {
    if (!(b instanceof HTMLElement)) continue;
    b.addEventListener('click', () => {
      options.setLinesVisible(allIds(), b.dataset['all'] === 'show');
    });
  }
  const panel = el('#panel');
  const toggle = el('#panel-toggle');
  const setOpen = (open: boolean): void => {
    panel.classList.toggle('collapsed', !open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('aria-label', open ? 'Close lines' : 'Show lines');
    toggle.textContent = open ? '×' : 'Show lines';
  };
  toggle.addEventListener('click', () => {
    setOpen(panel.classList.contains('collapsed'));
  });
  document.addEventListener(
    'click',
    (e) => {
      if (
        !window.matchMedia('(max-width: 680px)').matches ||
        panel.classList.contains('collapsed') ||
        !(e.target instanceof Element) ||
        panel.contains(e.target)
      )
        return;
      setOpen(false);
      // Dismissing the sheet should not also pick a train behind it.
      if (e.target.closest('#map') !== null) e.stopPropagation();
    },
    true,
  );
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !panel.classList.contains('collapsed')) {
      setOpen(false);
      if (window.matchMedia('(max-width: 680px)').matches && toggle.hidden === false) toggle.focus();
    }
  });

  function addGroups(groups: readonly RosterGroup[], accuracyGroups?: readonly LineGroup[]): void {
    GROUPS.push(...groups);
    ACCURACY_GROUPS.push(...(accuracyGroups ?? groups));
    rosterBuilt = false;
    render();
  }

  return { render, setScope, addGroups };
}
